// The dealer ranking, arithmetic only, in the browser.
//
// The edit form's map answers a question the form cannot answer in prose: where on the
// public page does this storefront actually land for a visitor standing here. That is a
// different answer for every point the reader drags to, so it has to be recomputed rather
// than rendered - which means the browser needs the same sums OnlineDealerListing and
// OnlineDealers::RankingService do, over the payload OnlineDealers::RankingMapPayload
// hands the page.
//
// Two copies of one ranking is a drift risk, and the guard against it is
// spec/fixtures/ranking_parity.json: every expected figure in it is computed by the real
// Ruby, a spec regenerates it and fails when it moves, and the test beside this file reads
// the same bytes. Change a weight in Ruby and both sides go red together.
//
// Nothing here touches the DOM, holds state, or says anything to anybody: no imports, no
// element lookups, no strings. Drawing the map is a Stimulus controller's job; deciding
// where a dealer lands is this file's, and keeping those apart is what makes the parity
// fixture possible at all.

// Cartography::EARTH_RADIUS_MILES. The one figure the payload does not carry - it belongs
// to the distance calculation rather than to the scale the form draws - so the fixture's
// distances are the only thing holding the two copies together.
const EARTH_RADIUS_MILES = 3959;

// ---- Radians, the way BigDecimal does them ----
//
// Cartography#to_radians is `degrees.to_d * PI / 180`, and the `.to_d` is not decoration.
// BigDecimal works from the SHORTEST DECIMAL of a Float - 40.7529, never the binary
// 40.752899999999999636202119290828704833984375 it is stored as - multiplies exactly, and
// rounds once when Math.sin finally asks for a Float. Doing the same sequence in doubles
// rounds three times, and the third rounding is where it matters:
// `to_radians(lngB) - to_radians(lngA)` for two branches a hundred yards apart subtracts
// two numbers near 1.29 to get one near 1.7e-6, so every digit lost upstream lands in the
// answer. Measured against Ruby, plain double arithmetic is exact at 2,449 miles and 2e-7
// miles wrong at eight thousandths of one.
//
// So the conversion is carried in exact integers and rounded to a double exactly once,
// which reproduces Ruby's own Float bit for bit. Everything after it - sin, cos, acos - is
// already Float on both sides, because Math.sin(BigDecimal) hands back a Float.
//
// The residue this cannot close is libm: V8 and glibc are each within an ulp of the true
// sin, cos and acos, and need not agree on which side. Below roughly three and a half
// miles the law of cosines amplifies that ulp past 1e-9 miles, because the cosine of the
// central angle sits within 1e-11 of 1 and acos divides by its sine. A pair that close
// agreeing to the last bit is luck the fixture is allowed to rely on and code is not.

// Math::PI as BigDecimal receives it: the sixteen digits of its shortest decimal, scaled.
const PI_DIGITS = 3141592653589793n;
const PI_SCALE = 15n;

// Digits kept before the single rounding to a double, which needs seventeen. Twenty-three
// spare, so truncating the exact ratio cannot reach the bit being rounded.
const GUARD_DIGITS = 40n;
const GUARD = 10n ** GUARD_DIGITS;

// Every shape Number#toString emits for a finite value, including the exponent form it
// switches to below 1e-6.
const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/i;

// A number as `digits / 10 ** scale`, which is what Float#to_d hands BigDecimal.
// Number#toString and Float#to_s both emit the shortest decimal that round-trips, so the
// two runtimes agree on the digits even where they disagree on how to format them.
//
// Anything that is not a finite number reads as zero, the way nil.to_d and ''.to_d do: a
// branch with no coordinates is placed at (0, 0), and CompletenessPolicy is what keeps it
// off the page.
function decimalOf(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return { digits: 0n, scale: 0n };

  const [, sign, whole, fraction = "", exponent = "0"] = DECIMAL.exec(String(number));
  let digits = BigInt(sign + whole + fraction);
  let scale = BigInt(fraction.length) - BigInt(exponent);

  // A negative scale is a power of ten in the numerator, and the denominators below have
  // to stay whole for the exact subtraction to work.
  if (scale < 0n) {
    digits *= 10n ** -scale;
    scale = 0n;
  }

  return { digits, scale };
}

// Radians as an exact ratio, so two of them can be subtracted without rounding first.
function radiansOf(degrees) {
  const { digits, scale } = decimalOf(degrees);

  return { numerator: digits * PI_DIGITS, denominator: 180n * 10n ** (scale + PI_SCALE) };
}

function difference(a, b) {
  return {
    numerator: a.numerator * b.denominator - b.numerator * a.denominator,
    denominator: a.denominator * b.denominator,
  };
}

// The one rounding, done by Number's own decimal parser rather than by hand: it is
// correctly rounded by specification, which is the whole property being bought here.
function toDouble({ numerator, denominator }) {
  return Number(`${(numerator * GUARD) / denominator}e-${GUARD_DIGITS}`);
}

// Float#clamp for the values this module can produce. NaN would fall through both
// comparisons here where Ruby's clamp raises - but neither side is handed one:
// decimalOf maps non-finite input to 0 and effectiveBrand filters before clamping.
function clamp(value, low, high) {
  if (value < low) return low;
  if (value > high) return high;

  return value;
}

/**
 * 'As the crow flies' miles between two points over a spherical earth, by the spherical
 * law of cosines - Cartography#calculate_distance, operation for operation.
 *
 * @param {number} latA latitude of the first point, in degrees
 * @param {number} lngA longitude of the first point, in degrees
 * @param {number} latB latitude of the second point, in degrees
 * @param {number} lngB longitude of the second point, in degrees
 * @returns {number} miles
 */
export function distanceMiles(latA, lngA, latB, lngB) {
  const lat1 = toDouble(radiansOf(latA));
  const lat2 = toDouble(radiansOf(latB));
  const longs = toDouble(difference(radiansOf(lngB), radiansOf(lngA)));
  const cosine =
    Math.sin(lat1) * Math.sin(lat2) + Math.cos(lat1) * Math.cos(lat2) * Math.cos(longs);

  // Floating point error pushes the cosine fractionally outside acos's [-1, 1] domain when
  // the two points coincide. Unclamped, Ruby would raise Math::DomainError there and
  // JavaScript would answer NaN - the worse of the two, because a distance that is
  // silently no distance ranks a dealer nowhere rather than stopping. Cartography clamps
  // for its own reason; this clamps to stay the same function.
  return Math.acos(clamp(cosine, -1, 1)) * EARTH_RADIUS_MILES;
}

/**
 * The same distance, in ordinary double arithmetic - for the map's per-cell pass, and for
 * nothing that has to agree with Ruby exactly.
 *
 * distanceMiles above buys its last bits with BigInt, and they are not free: 6,214ns a
 * call against 61ns for this one, a hundred and one times, paid once per branch per
 * listing per cell. A country-sized repaint over a fifty-listing field is 3.7 SECONDS of
 * that arithmetic, which is a map that cannot be panned.
 *
 * Measured against distanceMiles over 200,000 random global pairs, the worst disagreement
 * here is 3.0e-10 miles, or about half a micrometre - at range. Within a mile of the
 * visitor the law of cosines loses conditioning and the drift grows, to about 1e-4 miles
 * a few feet out; see tieMarginMiles, which is where that matters.
 *
 * The ranking reads a distance in two places, and that drift can only matter at the edge
 * of each: a band edge, where it can
 * flip ten points of score, and a score tie, where RankingService orders on the raw
 * distance and two near-identical figures can swap. Everywhere else a half-micrometre
 * cannot change the answer.
 *
 * So the policy is fast first, exact at the edges: nearestMiles recomputes with
 * distanceMiles when a distance sits on a band edge, and settleCloseTies does the same
 * when two tied distances sit within a margin of each other. Both the score and the
 * distance ordering are pinned to Ruby by the parity fixture, and this is only allowed to
 * be quick because those two guards hand the undecidable cases to the exact form.
 *
 * @param {number} latA latitude of the first point, in degrees
 * @param {number} lngA longitude of the first point, in degrees
 * @param {number} latB latitude of the second point, in degrees
 * @param {number} lngB longitude of the second point, in degrees
 * @returns {number} miles
 */
const RADIANS_PER_DEGREE = Math.PI / 180;

export function approximateDistanceMiles(latA, lngA, latB, lngB) {
  const lat1 = latA * RADIANS_PER_DEGREE;
  const lat2 = latB * RADIANS_PER_DEGREE;
  const longs = (lngB - lngA) * RADIANS_PER_DEGREE;
  const cosine =
    Math.sin(lat1) * Math.sin(lat2) + Math.cos(lat1) * Math.cos(lat2) * Math.cos(longs);

  // Clamped for the same reason distanceMiles clamps: acos of a cosine that rounded past 1
  // is NaN, and a distance that is silently no distance ranks a dealer nowhere.
  return Math.acos(clamp(cosine, -1, 1)) * EARTH_RADIUS_MILES;
}

/**
 * Miles to the nearest branch - OnlineDealerListing#user_distance, which is a minimum
 * across the listing's locations.
 *
 * approximateDistanceMiles rather than distanceMiles, which is the difference between a
 * map that can be panned and one that cannot: the exact form carries its radians in BigInt
 * to reproduce Ruby bit for bit and costs a hundred times as much, and this is called once
 * per branch per listing per cell. What it gives up is 3e-10 miles, which is recovered
 * where it could change an answer: at a band edge below, and in a close score tie by
 * settleCloseTies, which a caller runs on these figures before positionFor orders them.
 * See the note over approximateDistanceMiles.
 *
 * @returns {number|null} null when there are no branches, which #distance_points scores as
 *   no band at all rather than as the top one
 */
export function nearestMiles(branches, lat, lng, tiers) {
  let nearest = null
  let from = null

  for (let index = 0; index < branches.length; index += 1) {
    const branch = branches[index]
    const miles = approximateDistanceMiles(branch.lat, branch.lng, lat, lng)
    if (nearest === null || miles < nearest) {
      nearest = miles
      from = branch
    }
  }

  if (nearest === null) return null

  // The one place the two implementations can actually disagree, and they do: a band is
  // chosen by `miles <= max_miles`, so a distance sitting ON an edge is decided by the
  // last bit of the arithmetic. dealer_ranking.test.js walks the edges and catches the
  // 25-mile one at latitude 45 - approximate says the band above, exact says the band
  // below, and that is ten points of score.
  //
  // Nowhere else is worth the exact call: away from an edge the two agree to about 3e-10
  // miles, on cells tens of miles wide. So the margin below is not an error bound, it is
  // "close enough to an edge that the cheap answer cannot be trusted to pick a side" -
  // wide enough to swallow any drift, narrow enough that this branch is taken by
  // essentially no cell on the grid.
  return onTierEdge(nearest, tiers) ? distanceMiles(from.lat, from.lng, lat, lng) : nearest
}

// Wider than the ~3e-10 the two implementations differ by, so a distance this close to an
// edge is one the fast path must not be left to settle.
const TIER_EDGE_MARGIN_MILES = 1e-6

function onTierEdge(miles, tiers) {
  for (let index = 0; index < tiers.length; index += 1) {
    if (Math.abs(miles - tiers[index].max_miles) <= TIER_EDGE_MARGIN_MILES) return true
  }

  return false
}

// OnlineDealerListing#user_distance to the exact figure - the minimum of distanceMiles
// across the branches, null when there are none.
function exactNearestMiles(branches, lat, lng) {
  let nearest = null

  for (let index = 0; index < branches.length; index += 1) {
    const miles = distanceMiles(branches[index].lat, branches[index].lng, lat, lng)
    if (nearest === null || miles < nearest) nearest = miles
  }

  return nearest
}

/**
 * The other place the fast distance cannot be trusted to pick a side: a score tie, which
 * RankingService breaks on the exact distance. Two dealers whose fast distances sit within
 * the edge margin of the subject's could be ordered the other way round by Ruby, or reach
 * the id tiebreak where Ruby does not - so those, and only those, are measured again
 * exactly, the same bargain nearestMiles strikes at a band edge.
 *
 * Rivals further apart than the margin keep their fast figure even when the subject's is
 * replaced: 3e-10 miles of drift cannot carry a gap wider than 1e-6 across zero.
 *
 * The close tie that actually happens is not geometry, it is two listings on the SAME
 * branches - two storefronts of one account, sharing its warehouses and its brand figure.
 * That tie is close in every cell on the map, and paying the exact form for it per cell is
 * the repaint cost nearestMiles exists to avoid. It does not need paying: the same
 * coordinates give the same figure from either form, so the fast distances are already
 * equal and the tie already falls through to id, exactly as it does in Ruby. So listings
 * on the subject's own branch set are left alone, and the exact form is only reached for
 * a close tie between different coordinates - once per coordinate set, shared by every
 * rival standing on it.
 *
 * @param {number} subjectScore
 * @param {number|null} subjectMiles the subject's fast distance
 * @param {Array<{lat: number, lng: number}>} subjectBranches
 * @param {number[]} scores the field's scores
 * @param {Array<number|null>} miles the field's fast distances, parallel to scores -
 *   replaced in place where a close tie needs the exact figure
 * @param {Array<{branches: Array<{lat: number, lng: number}>}>} field parallel to scores
 * @param {number} lat the visitor's latitude
 * @param {number} lng the visitor's longitude
 * @returns {number|null} the subject's distance, exact when any tie needed it
 */
export function settleCloseTies(subjectScore, subjectMiles, subjectBranches, scores, miles, field, lat, lng) {
  if (subjectMiles === null) return subjectMiles

  const subjectKey = branchSetKey(subjectBranches)
  let close = null

  for (let index = 0; index < scores.length; index += 1) {
    if (scores[index] !== subjectScore || miles[index] === null) continue
    if (Math.abs(miles[index] - subjectMiles) > tieMarginMiles(miles[index], subjectMiles)) continue
    if (branchSetKey(field[index].branches) === subjectKey) continue

    if (close === null) close = []
    close.push(index)
  }

  if (close === null) return subjectMiles

  // Keyed by coordinate set, so rivals sharing branches - with each other or with the
  // subject - are measured once and handed the identical figure.
  const exact = new Map()
  const exactFor = (branches) => {
    const key = branchSetKey(branches)
    if (!exact.has(key)) exact.set(key, exactNearestMiles(branches, lat, lng))

    return exact.get(key)
  }

  for (let index = 0; index < scores.length; index += 1) {
    if (scores[index] === subjectScore && branchSetKey(field[index].branches) === subjectKey) {
      // Level with the subject, and staying level once it is measured exactly.
      if (miles[index] === subjectMiles) miles[index] = exactFor(subjectBranches)
    }
  }
  for (const index of close) miles[index] = exactFor(field[index].branches)

  return exactFor(subjectBranches)
}

// How close two fast distances must be before their order is handed to the exact form.
//
// Not one margin, because the fast form's error is not one size. The law of cosines is
// ill-conditioned as the angle closes: measured over 400,000 pairs, the drift is under
// 7e-9 miles past a mile out, but 3.5e-6 at a hundredth of a mile and 1e-4 at a few
// feet, where a pair whose exact distances are identical can come out of the fast form
// a hundred-thousandth of a mile apart. So inside a mile the margin opens to 1e-3, ten
// times the worst of that - and a visitor within a mile of two tied branches is a cell or
// two on the whole map, so the exact calls it costs are a handful.
//
// What no margin closes is the exact form itself at that range: Ruby and V8 each round
// sin, cos and acos within an ulp of the truth, and a few feet from a branch that ulp is
// worth as much as this drift. The note over PI_DIGITS says the same of the fixture.
const SHORT_RANGE_MILES = 1
const SHORT_RANGE_TIE_MARGIN_MILES = 1e-3

function tieMarginMiles(a, b) {
  return Math.min(a, b) < SHORT_RANGE_MILES ? SHORT_RANGE_TIE_MARGIN_MILES : TIER_EDGE_MARGIN_MILES
}

// A branch set as something two listings can be compared on, whatever order their rows
// were added in. Cached on the array, because the field's arrays are the payload's and do
// not change while a pass runs, and this is asked per cell.
const branchSetKeys = new WeakMap()

function branchSetKey(branches) {
  let key = branchSetKeys.get(branches)
  if (key === undefined) {
    key = branches.map((branch) => `${branch.lat},${branch.lng}`).sort().join(";")
    branchSetKeys.set(branches, key)
  }

  return key
}

/**
 * The proximity band a distance falls in - OnlineDealerListing#distance_points.
 *
 * @param {number|null} miles distance to the nearest branch, null when unmeasured
 * @param {Array<{max_miles: number, tier: number}>} tiers the payload's bands, ascending
 * @returns {number} the band's worth, 0 when there is no distance or none of them reaches it
 */
export function tierFor(miles, tiers) {
  // Ruby's guard is `return 0 unless user_distance`, where 0.0 is truthy: a visitor
  // standing on the doorstep scores the top band, not no band. Only the absence of a
  // measurement is a zero, so this tests for that and not for falsiness.
  if (miles === null || miles === undefined) return 0;

  // The first edge the distance fits under, which is the nearest band only while the
  // bands stay in order - RankingMapPayload sorts them, and its spec pins that.
  const band = tiers.find((candidate) => miles <= candidate.max_miles);

  return band ? band.tier : 0;
}

// OnlineDealerListing#effective_branding_points.
//
// The payload's own brand figures arrive clamped, but the subject's comes off a form field
// somebody is still typing into, which is where a 50, a -1 or an empty box gets in.
// Scoring one of those raw would let the ones column span more than nine and reach up into
// the tens, reordering a pair the distance band had already settled.
function effectiveBrand(brand, { brand_min: minimum, brand_max: maximum }) {
  const points = Math.trunc(Number(brand));

  return clamp(Number.isFinite(points) ? points : 0, minimum, maximum);
}

/**
 * What a listing is worth to the visitor it has been measured against, as one number -
 * OnlineDealerListing#ranking_score. Country is the hundreds column, distance the tens,
 * brand the ones.
 *
 * @param {{sameCountry: boolean, miles: number|null, brand: number}} listing
 * @param {object} constants the payload's `constants`
 * @returns {number}
 */
export function scoreFor({ sameCountry, miles, brand }, constants) {
  return (
    (sameCountry ? constants.country_points : 0) +
    constants.points_per_tier * tierFor(miles, constants.tiers) +
    effectiveBrand(brand, constants)
  );
}

/**
 * The position the subject would occupy once RankingService has cut the organic list to
 * what the house brand pins leave of the page and pushed those pins into it.
 *
 * The subject is assumed to be in the running: a listing that fails the sales gate or is
 * still incomplete never reaches the organic list, and answering that is the caller's job
 * rather than this sum's.
 *
 * @param {number} subjectScore
 * @param {number|null} subjectMiles distance to the subject's nearest branch - the first
 *   tiebreaker, see below
 * @param {number} subjectId the last tiebreaker
 * @param {number[]} competitorScores the organic field, pins already out of it and the
 *   subject not in it
 * @param {Array<number|null>} competitorMiles parallel to competitorScores
 * @param {number[]} competitorIds parallel to competitorScores - competitorIds[i] is
 *   competitorScores[i]'s listing id
 * @param {Array<{position: number}>} pins 1-based page positions, as the payload sends them
 * @param {number} maxDealers
 * @returns {number|null} the 1-based position, or null when the subject misses the page
 */
export function positionFor(
  subjectScore,
  subjectMiles,
  subjectId,
  competitorScores,
  competitorMiles,
  competitorIds,
  pins,
  maxDealers,
) {
  // RankingService.rank_dealers sorts on [-ranking_score, miles, id], and this asks the
  // identical three questions. Distance is five bands and brand strength ten points, so
  // an exact score tie is the common case: the nearer branch takes it, and only a tie on
  // distance as well falls through to id. An unmeasured distance is Ruby's
  // Float::INFINITY, behind every measured one.
  //
  // The map hands this nearestMiles' fast distance where Ruby has the exact one, so it
  // runs settleCloseTies first: any tie close enough for the drift between the two to
  // decide it is handed the exact figures instead.
  const mine = subjectMiles ?? Infinity;
  let index = 0;
  for (let i = 0; i < competitorScores.length; i += 1) {
    const score = competitorScores[i];
    if (score !== subjectScore) {
      if (score > subjectScore) index += 1;
      continue;
    }

    const miles = competitorMiles[i] ?? Infinity;
    if (miles < mine || (miles === mine && competitorIds[i] < subjectId)) index += 1;
  }

  // `ranked.take(MAX_DEALERS - featured.size)`. The pins have their positions reserved
  // before the organic list is cut, so the subject has to be inside what is left of it -
  // being eighth of eight organic dealers is off the page even though the page holds seven.
  const organicSlots = maxDealers - pins.length;
  if (index >= organicSlots) return null;

  let size = Math.min(competitorScores.length + 1, organicSlots);

  // Ascending, and it has to be: position 5 is only the fifth place once position 2 has
  // taken its own ahead of it. The clamp is the sparse case - a page with three organic
  // dealers cannot have a fifth position, so the pin goes last rather than nowhere - and
  // it reads the size the list has reached, not the size it started at.
  for (const { position } of [...pins].sort((a, b) => a.position - b.position)) {
    if (Math.min(position - 1, size) <= index) index += 1;
    size += 1;
  }

  // `top.take(MAX_DEALERS)` closes RankingService and can never cut here: the organic list
  // was already cut to what the pins left of the page, and each pin puts exactly one
  // position back. Said rather than repeated as a check that cannot fire.
  return index + 1;
}
